import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, error, expired, resend
  const [message, setMessage] = useState("");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus("resend");
      setMessage("Aucun token fourni. Vous pouvez demander un nouveau lien de validation.");
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMessage(data.message || "Votre compte est activé !");
        setUser(data.user);
        
        // Stocker le token et l'utilisateur
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        
        toast.success("Compte activé avec succès !");
        
        // Redirection automatique après 3 secondes
        setTimeout(() => {
          const role = data.user?.role;
          if (role === "SUPER_ADMIN") {
            navigate("/admin");
          } else if (role === "APPORTEUR") {
            navigate("/apporteur/dashboard");
          } else if (role === "ARTISTE" || role === "MANAGER") {
            // Récupérer le slug de l'artiste
            const slug = data.user?.artistSlug;
            if (slug) {
              navigate(`/${slug}/management`);
            } else {
              navigate("/");
            }
          } else {
            navigate("/");
          }
        }, 3000);
      } else {
        if (data.detail?.includes("expiré")) {
          setStatus("expired");
        } else {
          setStatus("error");
        }
        setMessage(data.detail || "Erreur lors de la validation");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setStatus("error");
      setMessage("Une erreur est survenue. Veuillez réessayer.");
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Veuillez entrer votre adresse email");
      return;
    }

    setResendLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Email envoyé !");
        setStatus("resend-success");
        setMessage("Si un compte existe avec cet email, un nouveau lien de validation a été envoyé.");
      } else {
        if (response.status === 429) {
          toast.error(data.detail || "Trop de demandes, veuillez patienter");
        } else {
          toast.error(data.detail || "Erreur lors de l'envoi");
        }
      }
    } catch (error) {
      console.error("Resend error:", error);
      toast.error("Une erreur est survenue");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#D4AF37]">Sen Artiste</h1>
          <p className="text-zinc-400 mt-2">Vérification de votre compte</p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 backdrop-blur-sm">
          {/* Loading State */}
          {status === "loading" && (
            <div className="text-center py-8" data-testid="verify-loading">
              <Loader2 className="w-16 h-16 text-[#D4AF37] animate-spin mx-auto mb-4" />
              <p className="text-zinc-300">Vérification en cours...</p>
            </div>
          )}

          {/* Success State */}
          {status === "success" && (
            <div className="text-center py-4" data-testid="verify-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Compte activé !</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              {user && (
                <p className="text-zinc-300 mb-4">
                  Bienvenue, <span className="text-[#D4AF37] font-medium">{user.firstName}</span> !
                </p>
              )}
              <p className="text-zinc-500 text-sm">Redirection automatique dans 3 secondes...</p>
            </div>
          )}

          {/* Error State */}
          {status === "error" && (
            <div className="text-center py-4" data-testid="verify-error">
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Erreur de validation</h2>
              <p className="text-zinc-400 mb-6">{message}</p>
              <Button
                onClick={() => setStatus("resend")}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-black"
              >
                Demander un nouveau lien
              </Button>
            </div>
          )}

          {/* Expired State */}
          {status === "expired" && (
            <div className="text-center py-4" data-testid="verify-expired">
              <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Lien expiré</h2>
              <p className="text-zinc-400 mb-6">
                Ce lien de validation a expiré. Demandez un nouveau lien ci-dessous.
              </p>
              <Button
                onClick={() => setStatus("resend")}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-black"
              >
                Demander un nouveau lien
              </Button>
            </div>
          )}

          {/* Resend Form */}
          {(status === "resend" || status === "resend-success") && (
            <div className="py-4" data-testid="verify-resend">
              <div className="text-center mb-6">
                <Mail className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">
                  {status === "resend-success" ? "Email envoyé !" : "Renvoyer le lien"}
                </h2>
                <p className="text-zinc-400 text-sm">
                  {status === "resend-success" 
                    ? "Vérifiez votre boîte mail (pensez aux spams)."
                    : "Entrez votre adresse email pour recevoir un nouveau lien de validation."}
                </p>
              </div>

              {status === "resend" && (
                <div className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Votre email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="resend-email-input"
                  />
                  <Button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-black"
                    data-testid="resend-button"
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      "Envoyer le lien"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Back to login */}
          <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
            <Link 
              to="/login" 
              className="text-zinc-400 hover:text-[#D4AF37] inline-flex items-center gap-2 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
